import { Box, Spinner, toast } from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { EquipmentType, PriceSheetMappingDocument, ProgramType, ProgramTypeLabels } from 'types';

import { percentageFormatter } from '../../../helpers/percentageFormatter';
import { isErrorWithErrors } from '../../../services/helpers';
import { useCreatePriceSheetMappingMutation, useGetPriceSheetMappingByIdQuery } from '../../../services/pricing';
import { MainContainer } from '../../MainContainer';
import PageHeader from '../../PageHeader';

import { MappingFormFactory } from './MappingFormFactory';

export const EditMapping = () => {
  const { id, programType = ProgramType.solar } = useParams<{ id: any; programType: ProgramType }>();
  const navigate = useNavigate();
  const { data: priceSheetMapping, isLoading: isPriceSheetMappingLoading } = useGetPriceSheetMappingByIdQuery({
    id,
  });

  const [createPriceSheetMapping] = useCreatePriceSheetMappingMutation();

  const initialValues = useMemo(() => {
    if (!priceSheetMapping) return;
    // HVAC, water heater and generator mappings have no construction adders at all.
    const adderMap: Record<string, any> = Object.fromEntries(
      ('constructionAdders' in priceSheetMapping ? (priceSheetMapping.constructionAdders ?? []) : []).map(
        (adder) => [adder.type, adder],
      ),
    );
    const buildSolarValues = () => {
      const mapping = priceSheetMapping as PriceSheetMappingDocument<ProgramType.solar>;
      return {
        organizationIds: [{ label: mapping.organizationName, value: mapping.organizationId }],
        state: { value: mapping.state, label: mapping.state },
        utilities: [{ label: mapping.utility.name, value: mapping.utility.lseId }],
        priceSheet: { value: mapping.priceSheetId, label: mapping.priceSheetName },
        escMin: {
          label: percentageFormatter.format(Number(mapping.escalationRateMin)),
          value: mapping.escalationRateMin,
        },
        escMax: {
          label: percentageFormatter.format(Number(mapping.escalationRateMax)),
          value: mapping.escalationRateMax,
        },
        pvOnlyMin: { label: mapping.pvOnlyMin, value: mapping.pvOnlyMin },
        pvOnlyMax: { label: mapping.pvOnlyCeiling, value: mapping.pvOnlyCeiling },
        absoluteCeiling: { label: mapping.absoluteCeiling, value: mapping.absoluteCeiling },
        electricalUpgrade: adderMap?.electricalUpgrade
          ? { label: adderMap.electricalUpgrade.name, value: adderMap.electricalUpgrade.id }
          : '',
        arbitrageBattery: adderMap?.arbitrageBattery
          ? { label: adderMap.arbitrageBattery.name, value: adderMap.arbitrageBattery.id }
          : '',
        arbitrageBatteryCost: adderMap?.arbitrageBatteryCost
          ? { label: adderMap.arbitrageBatteryCost.name, value: adderMap.arbitrageBatteryCost.id }
          : '',
        backupBattery: adderMap?.backupBattery
          ? { label: adderMap.backupBattery.name, value: adderMap.backupBattery.id }
          : '',
        backupBatteryPriceCaps: adderMap?.backupBatteryPriceCap
          ? { label: adderMap.backupBatteryPriceCap.name, value: adderMap.backupBatteryPriceCap.id }
          : '',
        batteryMinCapacity: adderMap?.batteryMinimumCapacity
          ? { label: adderMap.batteryMinimumCapacity.name, value: adderMap.batteryMinimumCapacity.id }
          : '',
        arbitrageBatteryPriceCaps: adderMap?.arbitrageBatteryPriceCap
          ? { label: adderMap.arbitrageBatteryPriceCap.name, value: adderMap.arbitrageBatteryPriceCap.id }
          : '',
        holdback: mapping.holdback ? { label: mapping.holdback.name, value: mapping.holdback.id } : '',
        zeroEscalatorCeiling: mapping.zeroEscalatorCeiling
          ? { label: mapping.zeroEscalatorCeiling, value: mapping.zeroEscalatorCeiling }
          : '',
        energyCommunity: mapping.energyCommunity
          ? { label: mapping.energyCommunity.name, value: mapping.energyCommunity.id }
          : '',
        backupBatteryKwh: adderMap.backupBatteryKwh
          ? { label: adderMap.backupBatteryKwh.name, value: adderMap.backupBatteryKwh.id }
          : '',
        ppwModifier: mapping.ppwModifier ? { label: mapping.ppwModifier.name, value: mapping.ppwModifier.id } : '',
        domesticContent: mapping.domesticContentModifier
          ? {
              label: mapping.domesticContentModifier.name,
              value: mapping.domesticContentModifier.id,
            }
          : '',
      };
    };
    switch (programType) {
      case ProgramType.doePr: {
        const mapping = priceSheetMapping as PriceSheetMappingDocument<ProgramType.doePr>;
        return {
          organizationIds: [{ label: mapping.organizationName, value: mapping.organizationId }],
          state: { value: mapping.state, label: mapping.state },
          utilities: [{ label: mapping.utility.name, value: mapping.utility.lseId }],
          priceSheet: { value: mapping.priceSheetId, label: mapping.priceSheetName },
          escMin: {
            label: percentageFormatter.format(Number(mapping.escalationRateMin)),
            value: mapping.escalationRateMin,
          },
          escMax: {
            label: percentageFormatter.format(Number(mapping.escalationRateMax)),
            value: mapping.escalationRateMax,
          },
          minSystemSize: { label: mapping.minSystemSize, value: mapping.minSystemSize },
          maxSystemSize: { label: mapping.maxSystemSize, value: mapping.maxSystemSize },
          minBatteryCapacity: {
            label: mapping.minBatteryCapacity,
            value: mapping.minBatteryCapacity,
          },
          maxBatteryCapacity: {
            label: mapping.maxBatteryCapacity,
            value: mapping.maxBatteryCapacity,
          },
          solarReadiness: adderMap?.solarReadiness
            ? { label: adderMap.solarReadiness.name, value: adderMap.solarReadiness.id }
            : '',
          backupBattery: adderMap?.backupBattery
            ? { label: adderMap.backupBattery.name, value: adderMap.backupBattery.id }
            : '',
        };
      }
      case ProgramType.hvac:
      case ProgramType.waterHeater:
      case ProgramType.geothermal:
      case ProgramType.generator: {
        const mapping = priceSheetMapping as PriceSheetMappingDocument<
          ProgramType.hvac | ProgramType.waterHeater | ProgramType.geothermal | ProgramType.generator
        >;
        return {
          organizationIds: [{ label: mapping.organizationName, value: mapping.organizationId }],
          states: [{ value: mapping.state, label: mapping.state }],
          priceSheet: { value: mapping.priceSheetId, label: mapping.priceSheetName },
          escMin: {
            label: percentageFormatter.format(Number(mapping.escalationRateMin)),
            value: mapping.escalationRateMin,
          },
          escMax: {
            label: percentageFormatter.format(Number(mapping.escalationRateMax)),
            value: mapping.escalationRateMax,
          },
          minTerm: { label: mapping.minTerm, value: mapping.minTerm },
          maxTerm: { label: mapping.maxTerm, value: mapping.maxTerm },
          minSystemCost: { label: mapping.minSystemCost, value: mapping.minSystemCost },
          maxSystemCost: { label: mapping.maxSystemCost, value: mapping.maxSystemCost },
        };
      }
      case ProgramType.consumerSolar: {
        const mapping = priceSheetMapping as PriceSheetMappingDocument<ProgramType.consumerSolar>;
        return {
          organizationIds: [{ label: mapping.organizationName, value: mapping.organizationId }],
          state: { value: mapping.state, label: mapping.state },
          utilities: [{ label: mapping.utility.name, value: mapping.utility.lseId }],
          priceSheet: { value: mapping.priceSheetId, label: mapping.priceSheetName },
          escMin: {
            label: percentageFormatter.format(Number(mapping.escalationRateMin)),
            value: mapping.escalationRateMin,
          },
          escMax: {
            label: percentageFormatter.format(Number(mapping.escalationRateMax)),
            value: mapping.escalationRateMax,
          },
          minSystemSize: { label: mapping.minSystemSize, value: mapping.minSystemSize },
          maxSystemSize: { label: mapping.maxSystemSize, value: mapping.maxSystemSize },
          minProductionFactor: {
            label: mapping.minProductionFactor,
            value: mapping.minProductionFactor,
          },
          maxProductionFactor: {
            label: mapping.maxProductionFactor,
            value: mapping.maxProductionFactor,
          },
          arbitrageBattery: adderMap?.arbitrageBattery
            ? { label: adderMap.arbitrageBattery.name, value: adderMap.arbitrageBattery.id }
            : '',
        };
      }
      case ProgramType.energyBackup: {
        const mapping = priceSheetMapping as PriceSheetMappingDocument<ProgramType.energyBackup>;
        return {
          organizationIds: [{ label: mapping.organizationName, value: mapping.organizationId }],
          state: { value: mapping.state, label: mapping.state },
          utilities: [{ label: mapping.utility.name, value: mapping.utility.lseId }],
          energyBackupOptions: {
            backupBattery: mapping.equipmentTypes.includes(EquipmentType.backupBattery),
            arbitrageBattery: !!adderMap?.arbitrageBatteryPriceCap,
          },
          priceSheetMappingId: mapping.id,
          minTerm: { label: mapping.minTerm, value: mapping.minTerm },
          maxTerm: { label: mapping.maxTerm, value: mapping.maxTerm },
          minSystemCost: { label: mapping.minSystemCost, value: mapping.minSystemCost },
          maxSystemCost: { label: mapping.maxSystemCost, value: mapping.maxSystemCost },
          priceSheet: { value: mapping.priceSheetId, label: mapping.priceSheetName },
          escMin: {
            label: percentageFormatter.format(Number(mapping.escalationRateMin)),
            value: mapping.escalationRateMin,
          },
          escMax: {
            label: percentageFormatter.format(Number(mapping.escalationRateMax)),
            value: mapping.escalationRateMax,
          },
          backupBatteryCap: adderMap?.backupBatteryPriceCap
            ? { label: adderMap.backupBatteryPriceCap.name, value: adderMap.backupBatteryPriceCap.id }
            : '',
          arbitrageBatteryCap: adderMap?.arbitrageBatteryPriceCap
            ? { label: adderMap.arbitrageBatteryPriceCap.name, value: adderMap.arbitrageBatteryPriceCap.id }
            : '',
        };
      }
      case ProgramType.solar:
      case ProgramType.newHomes:
      case ProgramType.newCommunity:
        return buildSolarValues();
      default:
        // `programType` comes from the URL, so an unrecognized value still has to render
        // something; the assert only guards against a new ProgramType being forgotten.
        programType satisfies never;
        return buildSolarValues();
    }
  }, [priceSheetMapping, programType]);

  const handleSubmit = async (data: any) => {
    try {
      await createPriceSheetMapping({ data, programType }).unwrap();
      toast.success('Mapping updated successfully');
      navigate(`/admin/pricing/${programType}`);
    } catch (e) {
      if (isErrorWithErrors(e)) {
        const errorMessage = e.data.message;
        toast.error(`${errorMessage}: ${e.data.errors.map((e) => e).join(',')}`);
      } else {
        console.error(e);
        toast.error('Error updating mapping');
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Edit {ProgramTypeLabels[programType]} Price Mapping</title>
      </Helmet>
      <MainContainer maxWidth="100">
        <PageHeader
          eyebrow={<Link to={`/admin/pricing/${programType}`}>Pricing</Link>}
          title={`Edit ${ProgramTypeLabels[programType]} Price Mapping`}
        />
        <Box padding={{ base: '0 lg', tablet: '0' }}>
          {!isPriceSheetMappingLoading && priceSheetMapping ? (
            <MappingFormFactory handleSubmit={handleSubmit} programType={programType} data={initialValues} />
          ) : (
            <Spinner size="lg" />
          )}
        </Box>
      </MainContainer>
    </>
  );
};
